import React from 'react';
import {
  Loader2, CheckCircle2, AlertTriangle, X, ArrowRight,
  SpellCheck, MessageSquare, Quote, Pilcrow
} from 'lucide-react';

const typeConfig = {
  spelling:    { icon: SpellCheck,    label: 'Spelling',    color: 'text-red-500' },
  grammar:     { icon: MessageSquare, label: 'Grammar',    color: 'text-orange-500' },
  preposition: { icon: Pilcrow,       label: 'Preposition', color: 'text-amber-500' },
  punctuation: { icon: Quote,         label: 'Punctuation', color: 'text-blue-500' },
  style:       { icon: AlertTriangle, label: 'Style',      color: 'text-purple-500' },
};

const GrammarCheckPanel = ({
  issues = [],
  isChecking = false,
  onReplace,
  onIgnore,
  onClose,
  isDarkMode,
  theme,
}) => {
  const pillStyle = { borderRadius: '9999px' };

  if (isChecking) {
    return (
      <div
        className={`p-6 rounded-sm border ${
          isDarkMode
            ? 'bg-stone-900/80 border-stone-800'
            : 'bg-white border-stone-100'
        }`}
      >
        <div className="flex items-center gap-3">
          <Loader2 size={16} className="animate-spin text-emerald-500" />
          <span className={`text-[10px] font-bold uppercase tracking-widest ${theme.textMuted}`}>
            Checking grammar...
          </span>
        </div>
      </div>
    );
  }

  if (issues.length === 0) return null;

  return (
    <div
      className={`rounded-sm border ${
        isDarkMode
          ? 'bg-stone-900/80 border-stone-800'
          : 'bg-white border-stone-100'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-stone-800/50 dark:border-stone-800">
        <div className="flex items-center gap-2.5">
          <CheckCircle2 size={14} className={issues.length === 0 ? 'text-emerald-500' : 'text-amber-500'} />
          <span className={`text-[10px] font-bold uppercase tracking-widest ${theme.text}`}>
            {issues.length === 0
              ? 'No issues found'
              : `${issues.length} ${issues.length === 1 ? 'issue' : 'issues'} found`}
          </span>
        </div>
        <button
          onClick={onClose}
          className="opacity-40 hover:opacity-100 transition-opacity"
        >
          <X size={14} />
        </button>
      </div>

      {/* Issues list */}
      {issues.length > 0 && (
        <div className="divide-y divide-stone-800/30 dark:divide-stone-800 max-h-64 overflow-y-auto">
          {issues.map((issue, index) => {
            const config = typeConfig[issue.type] || typeConfig.style;
            const Icon = config.icon;

            return (
              <div key={index} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Type badge */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon size={11} className={config.color} />
                      <span className={`text-[8px] font-bold uppercase tracking-widest ${config.color}`}>
                        {config.label}
                      </span>
                    </div>

                    {/* Original word (highlighted) */}
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs line-through ${
                        isDarkMode
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-red-50 text-red-600'
                      }`}
                    >
                      {issue.word}
                    </span>

                    {/* Arrow */}
                    <ArrowRight size={12} className="inline mx-2 opacity-40" />

                    {/* Suggestion */}
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        isDarkMode
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-emerald-50 text-emerald-600'
                      }`}
                    >
                      {issue.suggestion}
                    </span>

                    {/* Explanation */}
                    {issue.explanation && (
                      <p className={`text-[10px] opacity-50 mt-1.5 leading-relaxed ${theme.textMuted}`}>
                        {issue.explanation}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => onReplace(issue)}
                      style={pillStyle}
                      className={`px-3 py-1 text-[8px] font-bold uppercase tracking-widest border transition-all ${
                        isDarkMode
                          ? 'border-emerald-800 text-emerald-400 hover:bg-emerald-500/10'
                          : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                      }`}
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => onIgnore(index)}
                      style={pillStyle}
                      className={`px-3 py-1 text-[8px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity ${
                        theme.text
                      }`}
                    >
                      Ignore
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GrammarCheckPanel;
